import React from 'react';
import {
  Alert, AlertTitle, Box, Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useConfirm } from 'material-ui-confirm';
import { TripMatchStatusInfo } from '@marageti/z4-sdk/lib/travel';
import { useApiClient } from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';

const splitReasonAndComments = (text: string) => {
  const colonIndex = text.indexOf(':');
  const reason = text.substring(0, colonIndex).trim();
  const comments = text.substring(colonIndex + 1).trim();
  return { reason, comments };
};

// Comments block for the "canceled by X" alerts: shows the raw cancellation
// reason, or a legacy fallback when the trip predates this data.
const CanceledReasonComments = ({ statusInfo }: { statusInfo: TripMatchStatusInfo }) => (
  <Box sx={{ marginTop: 1.5 }}>
    <Typography variant="captionBold" color="primary.dark">Comments</Typography>
    {statusInfo.reason ? (
      <Typography>{statusInfo.reason}</Typography>
    ) : (
      <Typography>This is a legacy trip from D7, and is missing data on why this trip was canceled and by whom.</Typography>
    )}
  </Box>
);

// Comments block for sold/refunded cancellations: shows only the free-text
// comment from the cancellation form (after the refund-type prefix), hidden when empty.
const CancellationComments = ({ statusInfo }: { statusInfo: TripMatchStatusInfo }) => {
  if (!statusInfo?.reason) return null;
  const { comments } = splitReasonAndComments(statusInfo.reason);
  if (!comments) return null;
  return (
    <Box sx={{ marginTop: 1.5 }}>
      <Typography variant="captionBold" color="primary.dark">Comments</Typography>
      <Typography>{comments}</Typography>
    </Box>
  );
};

const CanceledByTravelerAlert = ({ statusInfo }: { statusInfo: TripMatchStatusInfo }) => (
  <Alert severity="error">
    <AlertTitle>Canceled Trip</AlertTitle>
    <Typography>
      This trip match has been canceled at the traveler&apos;s request. It will not be processed further.
    </Typography>
    <CanceledReasonComments statusInfo={statusInfo} />
  </Alert>
);

const MatchErrorAlert = ({ statusInfo }: { statusInfo: TripMatchStatusInfo }) => (
  <Alert severity="error">
    <AlertTitle>Canceled Trip</AlertTitle>
    <Typography>
      This trip match has been canceled due to a matching error. It will not be processed further.
    </Typography>
    <CanceledReasonComments statusInfo={statusInfo} />
  </Alert>
);

const CanceledByZicassoAlert = ({ statusInfo }: { statusInfo: TripMatchStatusInfo }) => (
  <Alert severity="error">
    <AlertTitle>Canceled Trip</AlertTitle>
    <Typography>
      This trip match has been canceled by Zicasso. It will not be processed further.
    </Typography>
    <CanceledReasonComments statusInfo={statusInfo} />
  </Alert>
);

const CanceledByPartnerAlert = ({ statusInfo }: { statusInfo: TripMatchStatusInfo }) => (
  <Alert severity="error">
    <AlertTitle>Canceled Trip</AlertTitle>
    <Typography>
      This trip match has been canceled at the partner&apos;s request. It will not be processed further.
    </Typography>
    <CanceledReasonComments statusInfo={statusInfo} />
  </Alert>
);

const BookingCanceledAlert = ({ statusInfo }: { statusInfo: TripMatchStatusInfo }) => (
  <Alert severity="error">
    <AlertTitle>Canceled Trip</AlertTitle>
    <Typography>
      This trip has been canceled without a refund from Zicasso&apos;s commission. Therefore, sales conversion will not be impacted.
    </Typography>
    <CancellationComments statusInfo={statusInfo} />
  </Alert>
);

const BookingRefundedAlert = ({ statusInfo }: { statusInfo: TripMatchStatusInfo }) => (
  <Alert severity="error">
    <AlertTitle>Canceled Trip</AlertTitle>
    <Typography>
      This trip has been canceled with a full or partial refund from Zicasso&apos;s commission,
      included as a credit on partner&apos;s upcoming invoice. Please note that canceled trips
      with refunds will impact sales conversion.
    </Typography>
    <CancellationComments statusInfo={statusInfo} />
  </Alert>
);

const RequestReview = ({
  reviewRequestHidden = false,
  tripMatchId,
  onReviewSent,
}: {
  tripMatchId: string,
  reviewRequestHidden?: boolean,
  onReviewSent?: () => void
}) => {
  const confirm = useConfirm();
  const apiClient = useApiClient();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(false);

  const sendReviewRequest = async () => {
    try {
      setIsLoading(true);
      await apiClient.tripMatchesClient.postReviewRequest(tripMatchId);
      enqueueSnackbar('Review request has been sent successfully', { variant: 'success' });
      if (onReviewSent) {
        onReviewSent();
      }
    } catch (error) {
      console.error('Error sending review request:', error);
      enqueueSnackbar('Failed to send review request', { variant: 'default' });
    } finally {
      setIsLoading(false);
    }
  };

  const onClickRequestReview = () => {
    if (reviewRequestHidden) {
      confirm({
        title: 'This Trip Has Been Flagged',
        description: 'The banner prompting the traveler to review this trip is hidden in the Trips app. Would you still like to request a review?',
        confirmationButtonProps: {
          color: 'primary',
        },
        cancellationText: 'No, Cancel',
        confirmationText: 'Yes, Request Review',
      })
        .then(() => {
          sendReviewRequest();
        })
        .catch(() => {});
    } else {
      sendReviewRequest();
    }
  };

  return (
    <Alert severity="success">
      <AlertTitle>Request a Review</AlertTitle>
      <span>
        The traveler has now returned from their trip. This is an excellent opportunity to collect their valuable feedback and review of their travel experience.
      </span>
      <LoadingButton
        onClick={onClickRequestReview}
        variant="contained"
        loading={isLoading}
      >
        Request Review
      </LoadingButton>
    </Alert>
  );
};

export {
  CanceledByTravelerAlert, MatchErrorAlert, CanceledByZicassoAlert, CanceledByPartnerAlert, BookingCanceledAlert, BookingRefundedAlert, RequestReview,
};
